import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

import Spinner from './spinner/Spinner';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';

function ModuleFAQForm({ toggleModal, faq }) {
  const [isLoading, setIsLoading] = useState(false);
  const { moduleId } = useParams();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { question: faq?.question || '', answer: faq?.answer || '' },
  });
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (data) => {
      if (faq) {
        return axiosInstancev2.put(`/module-faqs/${faq.id}`, data);
      }
      return axiosInstancev2.post('/module-faqs', data);
    },
    {
      onSuccess: (response) => {
        const { res } = response.data;
        if (res?.error) {
          toast.error(
            res.error || `Failed to ${faq ? 'update' : 'create'} FAQ`
          );
        } else {
          queryClient.invalidateQueries(['module-faqs', moduleId]);
          toast.success(`FAQ successfully ${faq ? 'updated' : 'created'}`);
          toggleModal();
        }
      },
      onError: (err) => {
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          `Failed to ${faq ? 'update' : 'create'} FAQ. Try again.`;
        toast.error(msg);
      },
    }
  );

  const onSubmit = async (requestData) => {
    if (!moduleId) {
      toast.error('Missing module id');
      return;
    }
    setIsLoading(true);

    mutation.mutate({ ...requestData, module_id: moduleId });
  };

  const borderError = (name) =>
    errors[name] ? 'border-red-600' : 'border-[#CBCBCB]';

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="flex flex-col space-y-1">
          <label htmlFor="faq-question" className="text-base">
            Type a question
          </label>
          <Controller
            name="question"
            control={control}
            rules={{ required: 'Question is required' }}
            render={({ field }) => (
              <textarea
                {...field}
                id="faq-question"
                placeholder=""
                className={`${borderError(
                  'question'
                )} border-[1px] px-2 py-2 rounded-lg focus:outline-none`}
              />
            )}
          />
          {errors.question && (
            <span className="text-red-600 text-xs mt-2">
              {errors.question?.message}
            </span>
          )}
        </div>
        <div className="flex flex-col space-y-1">
          <label htmlFor="faq-answer" className="text-base">
            Type an answer
          </label>
          <Controller
            name="answer"
            control={control}
            rules={{ required: 'Answer is required' }}
            render={({ field }) => (
              <textarea
                {...field}
                id="faq-answer"
                placeholder=""
                className={`${borderError(
                  'answer'
                )} border-[1px] px-2 py-2 rounded-lg focus:outline-none`}
              />
            )}
          />
          {errors.answer && (
            <span className="text-red-600 text-xs mt-2">
              {errors.answer?.message}
            </span>
          )}
        </div>
        {isLoading ? (
          <div className="grid justify-center items-center">
            <Spinner />
          </div>
        ) : (
          <div className=" flex justify-between mt-8">
            <button
              className={`border-[1px] border-claret-500 px-4 py-1 rounded-md text-claret-500 text-sm space-x-2 flex items-center `}
              type="button"
              onClick={toggleModal}
            >
              <CancelIcon fontSize="inherit" />
              <p>Cancel</p>
            </button>
            <button
              className={`bg-claret-500 px-4 py-1 rounded-md text-white text-sm space-x-2 flex items-center `}
              type="submit"
            >
              <SaveIcon fontSize="inherit" />
              <p>{faq ? 'Update' : 'Create'}</p>
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

export default ModuleFAQForm;
